import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, Loader2, Search, Trash2, Archive, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSalesOrderStore } from '../store/salesOrderStore';
import {
  useSalesOrderEventStream,
  onSalesOrderEvent,
  onSalesOrderStreamReconnect,
} from '../hooks/useSalesOrderEventStream';
import { SalesOrderEventType, SalesOrderStatus } from '../types/salesOrder.types';
import { SalesOrderStatusBadge } from '../components/SalesOrderStatusBadge';
import { SalesOrderReferenceSyncDialog } from '../components/SalesOrderReferenceSyncDialog';
import { salesOrderService } from '../services/salesOrderService';
import { statusesForFilter, rowMatchesFilter } from '../lib/listFilter';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { type FilterTab } from '../store/salesOrderWorkspaceStore';

export function SalesOrderListPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<FilterTab>('drafts');
  const [search, setSearch] = useState('');

  const list = useSalesOrderStore((s) => s.list);
  const isLoading = useSalesOrderStore((s) => s.isLoadingList);
  const loadList = useSalesOrderStore((s) => s.loadList);
  const spliceRow = useSalesOrderStore((s) => s.spliceRow);
  const removeRow = useSalesOrderStore((s) => s.removeRow);
  const archiveDraft = useSalesOrderStore((s) => s.archiveDraft);
  const deleteDraft = useSalesOrderStore((s) => s.deleteDraft);

  const canSyncReferenceData = useAuthStore((s) => s.hasPermission(PERMISSIONS.QUOTATIONS_SYNC_MASTER_DATA));
  const [syncOpen, setSyncOpen] = useState(false);
  const canArchive = useAuthStore((s) => s.hasPermission(PERMISSIONS.SALES_ORDERS_ARCHIVE_SALES_ORDER));
  const canDelete = useAuthStore((s) => s.hasPermission(PERMISSIONS.SALES_ORDERS_DELETE_SALES_ORDER));

  // SSE wiring — single connection per page; child handlers subscribe via the bus.
  // (The live/reconnecting badge was removed — the subscription stays so rows
  // still splice in real time, 86d3ckd1e.)
  useSalesOrderEventStream();

  const refresh = useCallback(() => {
    const params: Parameters<typeof loadList>[0] = {
      search: search.trim() || undefined,
      includeArchived: tab === 'archived',
    };
    const statuses = statusesForFilter(tab);
    if (statuses) params.statuses = statuses;
    void loadList(params);
  }, [tab, search, loadList]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const off = onSalesOrderEvent(async (evt) => {
      if (evt.eventType === SalesOrderEventType.Deleted) {
        removeRow(evt.salesOrderId);
        return;
      }
      if (evt.salesOrderId === 0) {
        refresh();    // bulk worker event — full refresh
        return;
      }
      try {
        const row = await salesOrderService.getRow(evt.salesOrderId);
        // 86d3bujyr — gate on filter membership so a row that left the active tab (e.g. a draft
        // that just posted → Submitted, or an archived row) drops off instead of lingering.
        if (rowMatchesFilter(row, tab)) spliceRow(row);
        else removeRow(row.id);
      } catch {
        // getRow failed (row deleted / no longer visible) — drop from list.
        removeRow(evt.salesOrderId);
      }
    });
    const offReconnect = onSalesOrderStreamReconnect(() => refresh());
    return () => {
      off();
      offReconnect();
    };
  }, [refresh, spliceRow, removeRow, tab]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sales Orders"
        description="Firm Offer drawdowns posted to NetSuite"
        actions={
          <>
            {/* FO-SOI-4: entry point to the master-detail workspace, now the
                primary surface (creation + editing happen there). */}
            <Button variant="outline" onClick={() => navigate('/sales-orders/workspace')}>
              <LayoutGrid className="h-4 w-4 mr-1.5" />
              Go to workspace
            </Button>
            {canSyncReferenceData && (
              <Button variant="outline" onClick={() => setSyncOpen(true)}>
                <Settings2 className="h-4 w-4 mr-1.5" />
                Configuration
              </Button>
            )}
          </>
        }
      />

      <div className="flex items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={(v) => setTab(v as FilterTab)}>
          <TabsList>
            <TabsTrigger value="drafts">Drafts</TabsTrigger>
            <TabsTrigger value="submitted">Submitted</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search SOD- / SO- / FO- / project / customer…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Document #</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>FO #</TableHead>
              <TableHead>NS SO #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Project</TableHead>
              <TableHead>Delivery Due</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && list.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                  Loading sales orders…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && list.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground">
                  No sales orders match the current filter.
                </TableCell>
              </TableRow>
            )}
            {list.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer hover:bg-accent"
                onClick={() => navigate(`/sales-orders/workspace?id=${row.id}&filter=${tab}`)}
              >
                <TableCell className="font-mono text-sm">{row.salesOrderNumber}</TableCell>
                <TableCell><SalesOrderStatusBadge status={row.status} /></TableCell>
                <TableCell className="font-mono text-xs">{row.linkedFirmOfferTranId || '—'}</TableCell>
                <TableCell className="font-mono text-xs">{row.netSuiteTranId || '—'}</TableCell>
                <TableCell>{row.customerName}</TableCell>
                <TableCell className="text-sm">{row.projectName}</TableCell>
                <TableCell className="text-sm">{row.deliveryDueDate || '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : new Date(row.createdAt).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {row.status === SalesOrderStatus.Draft && (
                    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {canArchive && !row.isArchived && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Archive"
                          onClick={() => {
                            if (confirm(`Archive ${row.salesOrderNumber}?`)) {
                              void archiveDraft(row.id);
                            }
                          }}
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Delete"
                          onClick={() => {
                            if (confirm(`Delete ${row.salesOrderNumber}?`)) {
                              void deleteDraft(row.id);
                            }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <SalesOrderReferenceSyncDialog open={syncOpen} onOpenChange={setSyncOpen} />
    </div>
  );
}
